import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import {
  Coins,
  ShoppingBag,
  Truck,
  Wallet,
  Sparkles,
  ArrowRight,
  Gift,
  Repeat,
  Ban,
  HelpCircle,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { TruCoinBadge } from "@/components/TruCoinBadge";
import { cn } from "@/lib/utils";
import {
  formatCoinCount,
  formatRupeesFromPaise,
  paiseToWholeCoins,
  POINT_VALUE_PAISE,
} from "@/lib/loyalty/coins";


export const Route = createFileRoute("/trucoins")({
  component: TruCoinsHowItWorks,
  head: () => ({
    meta: [
      { title: "How TruCoins Work. TruWe Loyalty Rewards" },
      {
        name: "description",
        content:
          "Earn TruCoins on every prepaid TruWe order and redeem them as discounts on future purchases. 1 TruCoin = ₹0.50. Learn how earning, pending and redemption work.",
      },
      {
        property: "og:title",
        content: "How TruCoins Work. TruWe Loyalty Rewards",
      },
      {
        property: "og:description",
        content:
          "Earn TruCoins on every prepaid TruWe order and redeem them as discounts on future purchases. 1 TruCoin = ₹0.50.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "TruWe" },
      { property: "og:locale", content: "en_IN" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@truwe" },
      {
        name: "twitter:title",
        content: "How TruCoins Work. TruWe Loyalty Rewards",
      },
      {
        name: "twitter:description",
        content:
          "Earn TruCoins on every prepaid TruWe order and redeem them as discounts on future purchases. 1 TruCoin = ₹0.50.",
      },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/trucoins" }],
  }),
});

const STEPS = [
  {
    icon: ShoppingBag,
    title: "Shop & pay",
    body: "Place a prepaid order. TruCoins are not earned on Cash on Delivery orders.",
  },
  {
    icon: Truck,
    title: "Pending coins",
    body: "Coins are shown as pending from order placement until your order is delivered.",
  },
  {
    icon: Wallet,
    title: "Available after 7 days",
    body: "TruCoins become available 7 days after delivery, so you can redeem them on your next order.",
  },
  {
    icon: Sparkles,
    title: "Redeem & save",
    body: "Toggle TruCoins at checkout on a future prepaid order and watch the discount apply instantly.",
  },
];

const FAQS = [
  {
    question: "How much is one TruCoin worth?",
    answer:
      "Each TruCoin gives a ₹0.50 discount on the pre-GST merchandise value of eligible TruWe products. For example, 20 TruCoins = ₹10 off and 500 TruCoins = ₹250 off.",
  },
  {
    question: "When do my TruCoins become available?",
    answer:
      "Earned TruCoins are pending from order placement until delivery, then become available 7 days after delivery. If the order is cancelled, returned, refunded, or marked RTO, the coins are reversed.",
  },
  {
    question: "Can I use TruCoins on COD orders?",
    answer:
      "No. TruCoins can only be earned and redeemed on fully prepaid orders. Shipping charges, COD fees, and GST are also not discounted.",
  },
  {
    question: "How many TruCoins can I redeem at once?",
    answer:
      "You can redeem at least 20 TruCoins (₹10 off) and at most 500 TruCoins (₹250 off) per order, capped at 20% of the eligible ex-GST merchandise value.",
  },
  {
    question: "Do TruCoins expire?",
    answer:
      "Yes. Available TruCoins expire 180 days after the date they became available. Expiry dates are shown in your account.",
  },
  {
    question: "Can I transfer or withdraw TruCoins?",
    answer:
      "No. TruCoins are not cash, a wallet balance, or a payment instrument. They cannot be transferred, sold, gifted, or withdrawn.",
  },
];

function TruCoinsHowItWorks() {
  return (
    <main className="min-h-screen bg-background">
      <HeroSection />
      <ValuePropSection />
      <LifecycleSection />
      <EarningRulesSection />
      <CalculatorSection />
      <FaqSection />
      <CtaSection />
    </main>
  );
}

function HeroSection() {
  return (
    <section className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(800px 600px at 80% 10%, color-mix(in oklab, var(--brand) 22%, transparent), transparent 60%), radial-gradient(600px 500px at 10% 90%, color-mix(in oklab, var(--brand) 14%, transparent), transparent 60%)",
        }}
      />
      <div className="container relative mx-auto px-4 text-center md:px-8">
        <div className="mx-auto mb-6 flex justify-center">
          <TruCoinBadge size="lg" className="float-soft" />
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-white/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand backdrop-blur">
          <Coins className="h-3.5 w-3.5" />
          TruWe Loyalty Programme
        </span>
        <h1 className="mx-auto mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-brand-deep md:text-6xl">
          Earn as you invest in your wellness
        </h1>

        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
          TruCoins turn every prepaid order into savings on your next one. No wallets, no gimmicks, just straightforward rewards for staying consistent.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/shop">
            <Button size="lg" className="rounded-full px-7">
              Start earning
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <Link to="/policies/trucoins-terms">
            <Button variant="outline" size="lg" className="rounded-full px-7">
              Read full terms
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}


function ValuePropSection() {
  return (
    <section className="border-y border-border bg-white/50 py-16 md:py-20">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid gap-6 sm:grid-cols-3">
          <ValueCard
            icon={Coins}
            title="₹0.50 per coin"
            body="Every TruCoin is worth fifty paise off the pre-GST value of eligible products."
            delay={0}
          />
          <ValueCard
            icon={Repeat}
            title="Earn on prepaid orders"
            body="Earn 5% back as TruCoins on every fully prepaid order."
            delay={0.1}
          />
          <ValueCard
            icon={Gift}
            title="Redeem on next order"
            body="Use your available TruCoins as a discount at checkout on future prepaid orders."
            delay={0.2}
          />
        </div>
      </div>
    </section>
  );
}

function ValueCard({
  icon: Icon,
  title,
  body,
  delay,
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  delay: number;
}) {
  return (
    
      <div className="glass-panel h-full rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-luxury">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand/10 text-brand">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="mt-4 font-display text-lg font-semibold text-brand-deep">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
    
  );
}

function LifecycleSection() {
  return (
    <section className="section-y container mx-auto px-4 md:px-8">
      
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">Lifecycle</span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-deep md:text-4xl">
            From order to redemption
          </h2>
          <p className="mt-3 text-muted-foreground">
            A simple, transparent journey. No hidden windows, no manual claims.
          </p>
        </div>
      

      <div className="relative mt-12">
        {/* Connecting line - desktop */}
        <div
          aria-hidden
          className="absolute top-11 left-0 right-0 hidden h-0.5 bg-gradient-to-r from-brand/20 via-brand/40 to-brand/20 md:block"
        />
        <div className="grid gap-8 md:grid-cols-4">
          {STEPS.map((step, i) => (
            <StepCard key={step.title} step={step} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, index }: { step: (typeof STEPS)[number]; index: number }) {
  const Icon = step.icon;
  return (
    
      <div className="relative text-center">
        <div className="relative z-10 mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-luxury ring-4 ring-brand-soft/60">
          <Icon className="h-7 w-7 text-brand" />
        </div>
        <div className="mt-5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">Step {index + 1}</span>
          <h3 className="mt-1 font-display text-lg font-semibold text-brand-deep">{step.title}</h3>
          <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">{step.body}</p>
        </div>
      </div>
    
  );
}

function EarningRulesSection() {
  return (
    <section className="bg-brand-soft/40 py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          
            <div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">Earning</span>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-deep md:text-4xl">
                How you earn TruCoins
              </h2>
              <p className="mt-4 text-muted-foreground">
                Earnings are calculated on the ex-GST product value after discounts, excluding GST, shipping, fees, and any amount already paid with TruCoins. Coins are rounded down to whole numbers.
              </p>
            </div>
          

          <div className="grid gap-4">
            <RuleCard
              title="5% standard earn rate"
              body="Earn 5% of the eligible ex-GST value as TruCoins on every prepaid order."
              delay={0.1}
            />
            <RuleCard
              title="500 TruCoins per-order cap"
              body="No matter how large the order, the maximum you can earn in one go is 500 TruCoins."
              delay={0.2}
            />
            <RuleCard
              title="No COD earning"
              body="Cash on Delivery orders do not earn or redeem TruCoins."
              icon={Ban}
              delay={0.3}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function RuleCard({
  title,
  body,
  highlight,
  icon: Icon,
  delay,
}: {
  title: string;
  body: string;
  highlight?: boolean;
  icon?: React.ElementType;
  delay: number;
}) {
  const DefaultIcon = Icon ?? (highlight ? Sparkles : Info);
  return (
    
      <div
        className={cn(
          "flex items-start gap-4 rounded-2xl border p-5 transition-all duration-300 hover:-translate-y-0.5",
          highlight
            ? "border-brand/30 bg-white shadow-luxury"
            : "border-border bg-white/70",
        )}
      >
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
            highlight ? "bg-brand text-white" : "bg-brand/10 text-brand",
          )}
        >
          <DefaultIcon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display text-base font-semibold text-brand-deep">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{body}</p>
        </div>
      </div>
    
  );
}

function CalculatorSection() {
  const [orderValue, setOrderValue] = useState<string>("1999");
  const [balance, setBalance] = useState<string>("250");

  const result = useMemo(() => {
    const orderPaise = Math.round((Number(orderValue) || 0) * 100);
    const balancePaise = (Number(balance) || 0) * POINT_VALUE_PAISE;
    const maxByPercent = Math.floor(orderPaise * 0.2);
    const maxByCap = 500 * POINT_VALUE_PAISE;
    const maxByBalance = balancePaise;
    const maxRedeemPaise = Math.max(0, Math.min(maxByPercent, maxByCap, maxByBalance));
    const minRedeemPaise = 20 * POINT_VALUE_PAISE;
    const redeemPaise = maxRedeemPaise >= minRedeemPaise ? maxRedeemPaise : 0;
    return {
      redeemPaise,
      coins: paiseToWholeCoins(redeemPaise),
      discount: formatRupeesFromPaise(redeemPaise),
      final: formatRupeesFromPaise(Math.max(0, orderPaise - redeemPaise)),
    };
  }, [orderValue, balance]);

  return (
    <section className="section-y container mx-auto px-4 md:px-8">
      
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">Try it</span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-deep md:text-4xl">
            See how much you could save
          </h2>
          <p className="mt-3 text-muted-foreground">
            Enter an order value and your TruCoins balance. The calculator applies the minimum redemption, 500-coin cap, and 20% order-value cap.
          </p>
        </div>
      

      
        <div className="mx-auto mt-10 max-w-3xl rounded-3xl border border-border bg-white p-6 shadow-luxury md:p-10">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="orderValue" className="block text-sm font-semibold text-brand-deep">
                Order value (₹, ex-GST)
              </label>
              <input
                id="orderValue"
                type="number"
                min={0}
                value={orderValue}
                onChange={(e) => setOrderValue(e.target.value)}
                className="mt-2 block w-full rounded-xl border border-input bg-background px-4 py-3 text-sm font-medium text-brand-deep outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div>
              <label htmlFor="coinBalance" className="block text-sm font-semibold text-brand-deep">
                Your TruCoins balance
              </label>
              <input
                id="coinBalance"
                type="number"
                min={0}
                step={1}
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                className="mt-2 block w-full rounded-xl border border-input bg-background px-4 py-3 text-sm font-medium text-brand-deep outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <ResultBox label="Order value" value={formatRupeesFromPaise(Math.round((Number(orderValue) || 0) * 100))} />
            <ResultBox label="TruCoins redeemed" value={result.coins > 0 ? `${formatCoinCount(result.redeemPaise)} coins` : "-"} />
            <ResultBox label="Discount applied" value={result.coins > 0 ? result.discount : "-"} highlight />
          </div>

          <div className="mt-6 rounded-2xl bg-brand-soft/60 p-4 text-center text-sm text-brand-deep">
            {result.coins > 0 ? (
              <>
                You pay{" "}
                <span className="font-display text-lg font-bold text-brand">{result.final}</span>{" "}
                after redeeming {formatCoinCount(result.redeemPaise)} TruCoins.
              </>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Info className="h-4 w-4" />
                Redemption needs at least 20 TruCoins and an order of ₹999+ ex-GST, capped at 20% of the order value.
              </span>
            )}
          </div>
        </div>
      
    </section>
  );
}

function ResultBox({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div
      className={cn(
        "rounded-2xl border p-4 text-center transition-all",
        highlight ? "border-brand/30 bg-brand-soft/40" : "border-border bg-background",
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className={cn("mt-1 font-display text-lg font-bold", highlight ? "text-brand" : "text-brand-deep")}>{value}</p>
    </div>
  );
}

function ReferSection() {
  return (
    <section className="relative overflow-hidden bg-brand-deep py-16 text-white md:py-24">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(700px 500px at 20% 20%, rgba(30,94,255,0.25), transparent 60%), radial-gradient(500px 400px at 85% 80%, rgba(30,94,255,0.15), transparent 60%)",
        }}
      />
      <div className="container relative mx-auto px-4 md:px-8">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] backdrop-blur">
                <Gift className="h-3.5 w-3.5" />
                Refer & Earn (Beta)
              </span>
              <h2 className="mt-5 font-display text-3xl font-bold tracking-tight md:text-4xl">
                Share the wellness, both of you earn
              </h2>
              <p className="mt-4 text-white/70">
                Invite friends to TruWe. When they place their first eligible prepaid order, they get a welcome discount and you get TruCoins credited after delivery plus 7 days.
              </p>
            </div>
          

          <div className="grid gap-4">
            <ReferCard
              title="They get ₹100 off + 200 TruCoins"
              body="Your friend receives ₹100 off their first eligible prepaid order of ₹1,000+ ex-GST, plus a one-time 200 TruCoins signup credit."
              delay={0.2}
            />
            <ReferCard
              title="You earn 200 TruCoins per referral"
              body="You get a flat 200 TruCoins (₹100 value) for every qualified referral, credited 7 days after their first order is delivered. Max 5 qualified referrals per 30-day window."
              delay={0.25}
            />
            <ReferCard
              title="Fair use only"
              body="No self-referral, duplicate accounts, or resale-driven referrals. Rewards are reversed for cancelled, refunded, RTO, or fraudulent orders."
              delay={0.3}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ReferCard({ title, body, delay }: { title: string; body: string; delay: number }) {
  return (
    
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10">
        <h3 className="font-display text-base font-semibold text-white">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-white/70">{body}</p>
      </div>
    
  );
}

function FaqSection() {
  return (
    <section className="section-y container mx-auto px-4 md:px-8">
      
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">FAQ</span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-deep md:text-4xl">
            Common questions
          </h2>
        </div>
      

      
        <div className="mx-auto mt-10 max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-border">
                <AccordionTrigger className="text-left font-display text-base font-semibold text-brand-deep hover:no-underline">
                  <span className="flex items-center gap-3">
                    <HelpCircle className="h-4 w-4 text-brand" />
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      
    </section>
  );
}

function CtaSection() {
  return (
    <section className="border-t border-border bg-white/50 py-16 md:py-24">
      <div className="container mx-auto px-4 text-center md:px-8">
        
          <div className="mx-auto flex max-w-2xl flex-col items-center">
            <TruCoinBadge size="md" className="mb-6" />
            <h2 className="font-display text-3xl font-bold tracking-tight text-brand-deep md:text-4xl">
              Ready to start earning TruCoins?
            </h2>
            <p className="mt-3 text-muted-foreground">
              Every prepaid order brings you closer to your next discount. No hidden steps, no waiting on support.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
              <Link to="/shop">
                <Button size="lg" className="rounded-full px-7">
                  Shop TruWe
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/account">
                <Button variant="outline" size="lg" className="rounded-full px-7">
                  View my TruCoins
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">
              For full programme details, read the{" "}
              <Link to="/policies/trucoins-terms" className="text-brand underline">
                TruCoins Programme Terms
              </Link>
              .
            </p>
          </div>
        
      </div>
    </section>
  );
}
